import axios from 'axios';

// const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
// const API_URL = process.env.REACT_APP_API_URL || 'http://192.168.178.47:8000/api';
const API_URL = process.env.REACT_APP_API_URL || 'https://ideas.y02.nl/api';

// Create a single Axios instance
const axiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 120000, // 120 seconds to match backend timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Store a single refresh promise to prevent multiple simultaneous refreshes
let refreshPromise = null;

export const login = async (username, password) => {
  try {
    const response = await axiosInstance.post(
      '/auth/login',
      new URLSearchParams({ username, password }),
      {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      }
    );
    const { access_token, refresh_token, is_admin } = response.data;
    console.log('Login: Storing tokens', { access_token, refresh_token, is_admin });
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('is_admin', is_admin);
    console.log('Login: localStorage after login', {
      access_token: localStorage.getItem('access_token'),
      refresh_token: localStorage.getItem('refresh_token'),
      is_admin: localStorage.getItem('is_admin'),
    });
    return response.data;
  } catch (err) {
    console.error('Login error:', err.response?.data || err.message);
    throw new Error(err.response?.data?.detail || err.message || 'Unknown login error');
  }
};

export const refreshToken = async () => {
  try {
    const refresh_token = localStorage.getItem('refresh_token');
    if (!refresh_token) {
      console.error('No refresh token found in localStorage');
      throw new Error('No refresh token available');
    }
    console.log('Refresh: Sending request with refresh_token:', refresh_token);
    const response = await axiosInstance.post('/auth/refresh', { refresh_token });
    const { access_token, refresh_token: new_refresh_token, is_admin } = response.data;
    console.log('Refresh: Received new tokens', { access_token, new_refresh_token, is_admin });
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', new_refresh_token);
    localStorage.setItem('is_admin', is_admin);
    console.log('Refresh: localStorage after refresh', {
      access_token: localStorage.getItem('access_token'),
      refresh_token: localStorage.getItem('refresh_token'),
      is_admin: localStorage.getItem('is_admin'),
    });
    return access_token;
  } catch (err) {
    console.error('Refresh token error:', {
      message: err.message,
      response: err.response?.data,
      status: err.response?.status,
      headers: err.response?.headers,
    });
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('is_admin');
    console.log('Refresh: Cleared localStorage due to error');
    throw new Error('Session expired, please log in again');
  }
};

export const logout = async () => {
  try {
    const refresh_token = localStorage.getItem('refresh_token');
    if (refresh_token) {
      await axiosInstance.post(
        '/auth/revoke',
        { refresh_token },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        }
      );
      console.log('Logout: Successfully revoked refresh token');
    }
  } catch (err) {
    console.error('Logout error:', err.response?.data || err.message);
  } finally {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('is_admin');
    console.log('Logout: Cleared localStorage');
    window.location.href = '/login';
  }
};

// Axios interceptor for handling 401 errors and token refresh
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        // Check if access_token exists
        const access_token = localStorage.getItem('access_token');
        if (!access_token) {
          console.error('No access token available');
          // Check if the original request had an Authorization header
          const hasAuthHeader = originalRequest.headers?.Authorization;

          // If there was no auth header, this request was meant to work without auth
          if (!hasAuthHeader) {
            return Promise.reject(error);
          }

          // If there was an auth header but no token, clear tokens and redirect
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('is_admin');
          window.location.href = '/login';
          return Promise.reject(error);
        }

        // Log the reason for 401 to debug
        console.log('401 error details:', {
          url: originalRequest.url,
          method: originalRequest.method,
          headers: originalRequest.headers,
          response: error.response?.data,
        });

        // If a refresh is already in progress, wait for it
        if (refreshPromise) {
          console.log('Waiting for existing refresh promise');
          await refreshPromise;
          const new_access_token = localStorage.getItem('access_token');
          if (!new_access_token) {
            console.error('No access token after refresh, redirecting to login');
            window.location.href = '/login';
            return Promise.reject(error);
          }
          console.log('Retrying with new access token:', new_access_token);
          originalRequest.headers['Authorization'] = `Bearer ${new_access_token}`;
          return axiosInstance(originalRequest);
        }

        // Start a new refresh with a slight delay
        console.log('Starting new refresh token request');
        refreshPromise = new Promise((resolve, reject) => {
          setTimeout(async () => {
            try {
              const new_access_token = await refreshToken();
              resolve(new_access_token);
            } catch (refreshError) {
              reject(refreshError);
            }
          }, 100); // 100ms delay to prevent rapid retries
        });

        const new_access_token = await refreshPromise;
        refreshPromise = null; // Clear the promise after completion
        console.log('Retrying with new access token:', new_access_token);
        originalRequest.headers['Authorization'] = `Bearer ${new_access_token}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        refreshPromise = null; // Clear the promise on failure
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('is_admin');

        // Check if this is a public endpoint that could work without auth
        const isPublicEndpoint = originalRequest.url?.includes('/ideas') ||
                                 originalRequest.url?.includes('/components') ||
                                 originalRequest.url?.includes('/files');

        if (isPublicEndpoint) {
          // For public endpoints, try again without the Authorization header
          console.log('Retrying public endpoint without auth after refresh failure');
          delete originalRequest.headers.Authorization;
          return axiosInstance(originalRequest);
        } else {
          // For private endpoints, redirect to login
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }
    }
    console.error('API error:', {
      url: originalRequest?.url,
      method: originalRequest?.method,
      status: error.response?.status,
      response: error.response?.data,
    });
    return Promise.reject(error);
  }
);

export default axiosInstance;